import React, { useState, useEffect, useMemo } from "react";
import {
   collection,
   query,
   getDocs,
   doc,
   writeBatch,
   serverTimestamp,
   onSnapshot,
   where
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import { FiLayout, FiUsers, FiCalendar, FiCheck, FiX, FiChevronLeft, FiChevronRight, FiCheckCircle, FiInfo, FiLayers } from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { useColorMode } from "@chakra-ui/react";
import { Floor, Seat } from "./ManagerFloorView";
import { getThemeClasses } from "../../../utils/themeUtils";
import { showToast } from "../../../utils/toastUtils";

interface ManagerAssignmentViewProps {
   floors: Floor[];
   currentOrgId: string;
   onRefreshBookings: () => void;
   users: any[];
}

interface Booking {
   id: string;
   seatId: string;
   floorId: string;
   userId: string;
   date: string;
   status: string;
}

const ManagerAssignmentView: React.FC<ManagerAssignmentViewProps> = ({ floors, currentOrgId, onRefreshBookings, users }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const theme = getThemeClasses(isDark);

   // --- State ---
   const [selectedDate, setSelectedDate] = useState<Date>(new Date());
   const [pickedDates, setPickedDates] = useState<string[]>([]); // Strings "YYYY-MM-DD"
   const [selectedFloorId, setSelectedFloorId] = useState<string>("");
   const [selectedSeatIds, setSelectedSeatIds] = useState<string[]>([]);
   const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
   const [userSearch, setUserSearch] = useState("");
   const [existingBookings, setExistingBookings] = useState<Booking[]>([]);
   const [assignments, setAssignments] = useState<any[]>([]); // New: Store floor assignments
   const [isBooking, setIsBooking] = useState(false);

   // --- Date Helpers ---
   const toDateStr = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
   };

   const getDaysInMonth = (date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const days = [];
      for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
      for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
      return days;
   };

   const changeMonth = (offset: number) => {
      const next = new Date(selectedDate);
      next.setMonth(next.getMonth() + offset);
      setSelectedDate(next);
   };

   const changeYear = (offset: number) => {
      const next = new Date(selectedDate);
      next.setFullYear(next.getFullYear() + offset);
      setSelectedDate(next);
   };

   // --- Data Fetching ---
   useEffect(() => {
      if (!currentOrgId) return;

      // Fetch Bookings
      const q = query(collection(db, `organization/${currentOrgId}/bookings`));
      const unsubBookings = onSnapshot(q, (snap) => {
         const books = snap.docs.map(d => ({ id: d.id, ...d.data() })) as Booking[];
         setExistingBookings(books);
      });

      // Fetch Floor Assignments to check availability
      const assignmentsRef = collection(db, `organization/${currentOrgId}/floor_assignments`);
      const unsubAssignments = onSnapshot(assignmentsRef, (snap) => {
         const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
         setAssignments(data);
      });

      return () => {
         unsubBookings();
         unsubAssignments();
      };
   }, [currentOrgId]);

   // --- Logic ---
   const toggleDate = (date: Date) => {
      const str = toDateStr(date);

      // Check validation: Is there any floor assigned to this date?
      const isAvailable = assignments.some(a => a.date === str);

      if (!isAvailable) {
         showToast({
            title: "Date Unavailable",
            description: "No floors have been assigned to this date in Date Assignments.",
            status: "error",
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      // Multi-select toggle
      setPickedDates(prev => {
         if (prev.includes(str)) {
            return prev.filter(d => d !== str);
         } else {
            return [...prev, str];
         }
      });
   };

   const toggleSeat = (seatId: string) => {
      setSelectedSeatIds(prev => {
         const newSelection = prev.includes(seatId) ? prev.filter(s => s !== seatId) : [...prev, seatId];
         // If less seats than users, trim users
         if (newSelection.length < selectedUserIds.length) {
            setSelectedUserIds(current => current.slice(0, newSelection.length));
         }
         return newSelection;
      });
   };

   const toggleUser = (userId: string) => {
      setSelectedUserIds(prev => {
         if (prev.includes(userId)) {
            return prev.filter(u => u !== userId);
         } else {
            if (prev.length >= selectedSeatIds.length) {
               showToast({
                  title: "Selection Limit Reached",
                  description: `You have selected ${selectedSeatIds.length} seats. You cannot select more users.`,
                  status: "warning",
                  duration: 2000
               });
               return prev;
            }
            return [...prev, userId];
         }
      });
   };

   const availableUsers = useMemo(() => {
      if (pickedDates.length === 0) return users;
      const busyUserIds = new Set<string>();
      existingBookings.forEach(b => {
         if (pickedDates.includes(b.date) && b.status !== 'cancelled') {
            busyUserIds.add(b.userId);
         }
      });
      return users.filter(u => !busyUserIds.has(u.id));
   }, [users, existingBookings, pickedDates]);

   const currentFloor = floors.find(f => f.id === selectedFloorId);

   const isSeatAvailable = (seatId: string) => {
      if (pickedDates.length === 0) return true;
      const conflict = existingBookings.find(b =>
         b.floorId === selectedFloorId &&
         b.seatId === seatId &&
         pickedDates.includes(b.date) &&
         b.status !== 'cancelled'
      );
      return !conflict;
   };

   const handleBooking = async () => {
      setIsBooking(true);
      const batch = writeBatch(db);
      try {
         pickedDates.forEach(date => {
            selectedUserIds.forEach((userId, index) => {
               const seatId = selectedSeatIds[index];
               const seat = currentFloor?.seats?.find(s => s.id === seatId);
               const user = users.find(u => u.id === userId);

               if (seat && user) {
                  const bookingId = `${seatId}_${date}`;
                  const ref = doc(db, `organization/${currentOrgId}/bookings`, bookingId);
                  batch.set(ref, {
                     seatId: seat.id,
                     seatName: seat.name,
                     floorId: selectedFloorId,
                     floorName: currentFloor?.name,
                     userId: user.id,
                     userName: `${user.firstName} ${user.lastName}`,
                     userEmail: user.email || "",
                     date: date,
                     status: 'active',
                     createdAt: serverTimestamp(),
                     createdBy: 'manager_manual_assignment'
                  });
               }
            });
         });
         await batch.commit();
         showToast({ title: "Bookings created successfully!", status: "success" });
         onRefreshBookings();
         setSelectedUserIds([]);
         setSelectedSeatIds([]);
         setPickedDates([]);
      } catch (err) {
         console.error(err);
         showToast({ title: "Booking failed", status: "error" });
      } finally {
         setIsBooking(false);
      }
   };

   // Stats
   const currentMonthIdx = selectedDate.getMonth();
   const currentYear = selectedDate.getFullYear();
   const monthlyBookings = existingBookings.filter(b => {
      const d = new Date(b.date);
      return d.getMonth() === currentMonthIdx && d.getFullYear() === currentYear && b.status !== 'cancelled';
   });

   return (
      <div className="flex flex-col gap-4 animate-in fade-in duration-300">

         {/* Top Section: Stats Cards (30%) & Calendar (70%) */}
         <div className="grid grid-cols-1 lg:grid-cols-[30%_70%] gap-4">
            {/* Stats */}
            <div className="flex flex-col gap-4">
               <div className={`${theme.card} border ${theme.border} rounded-2xl p-4 relative overflow-hidden group hover:shadow-md transition-all`}>
                  <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${isDark ? 'bg-[#B59143]' : 'bg-[#FBD282]'} group-hover:opacity-30 transition-opacity`}></div>
                  <div className="relative z-10">
                     <div className="flex items-center justify-between mb-3">
                        <div className={`p-2.5 rounded-lg ${isDark ? 'bg-[#B59143]/20' : 'bg-[#FBD282]/30'}`}>
                           <IconWrapper icon={FiLayers} size={18} className="text-[#B59143]" />
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This Month</span>
                     </div>
                     <h3 className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{monthlyBookings.length}</h3>
                     <p className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Active Bookings</p>
                     <div className={`mt-3 pt-3 border-t ${isDark ? 'border-[#2E2F33]' : 'border-gray-100'}`}>
                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'} font-medium`}>
                           {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </p>
                     </div>
                  </div>
               </div>

               <div className={`${theme.card} border ${theme.border} rounded-2xl p-4 relative overflow-hidden`}>
                  <div className="flex items-center justify-between mb-2">
                     <span className={`text-xs font-bold ${theme.text}`}>Selection</span>
                     <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>Live</span>
                  </div>
                  <div className="space-y-2">
                     <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500">Dates</span>
                        <span className={`font-bold ${pickedDates.length > 0 ? 'text-[#B59143]' : 'text-gray-400'}`}>{pickedDates.length}</span>
                     </div>
                     <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500">Seats</span>
                        <span className={`font-bold ${selectedSeatIds.length > 0 ? 'text-[#B59143]' : 'text-gray-400'}`}>{selectedSeatIds.length}</span>
                     </div>
                     <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500">Users</span>
                        <span className={`font-bold ${selectedUserIds.length > 0 ? 'text-[#B59143]' : 'text-gray-400'}`}>{selectedUserIds.length}</span>
                     </div>
                  </div>
                  <button
                     onClick={handleBooking}
                     disabled={isBooking || pickedDates.length === 0 || selectedSeatIds.length === 0 || selectedUserIds.length === 0}
                     className="w-full mt-4 py-2.5 bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white text-xs font-black uppercase tracking-wider rounded-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                     {isBooking ? 'Processing...' : 'Confirm Booking'}
                  </button>
               </div>
            </div>

            {/* Usage of FloorAssignmentManager Calendar Layout */}
            <div className={`${theme.card} border ${theme.border} rounded-2xl p-4`}>
               <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                     <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-gradient-to-br from-[#FBD282]/20 to-[#B59143]/20' : 'bg-gradient-to-br from-[#FBD282] to-[#B59143]'} flex items-center justify-center`}>
                        <IconWrapper icon={FiCalendar} size={20} className={isDark ? 'text-[#FBD282]' : 'text-white'} />
                     </div>
                     <div>
                        <h2 className={`text-base font-bold ${theme.text}`}>
                           {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h2>
                        <p className={`text-[10px] ${theme.textMuted} font-semibold`}>Select dates for booking</p>
                     </div>
                  </div>
                  <div className="flex items-center gap-2">
                     <div className="flex items-center gap-1">
                        <button onClick={() => changeYear(-1)} className={`px-2 py-1 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-md text-[10px] font-bold ${theme.textMuted}`}>Last Year</button>
                        <button onClick={() => changeYear(1)} className={`px-2 py-1 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-md text-[10px] font-bold ${theme.textMuted}`}>Next Year</button>
                     </div>
                     <div className={`w-px h-6 ${isDark ? 'bg-[#2E2F33]' : 'bg-gray-300'}`}></div>
                     <div className="flex items-center gap-1">
                        <button onClick={() => changeMonth(-1)} className={`p-1.5 rounded-lg ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} text-gray-400 hover:text-[#B59143] transition-colors`}><IconWrapper icon={FiChevronLeft} size={14} /></button>
                        <button onClick={() => changeMonth(1)} className={`p-1.5 rounded-lg ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} text-gray-400 hover:text-[#B59143] transition-colors`}><IconWrapper icon={FiChevronRight} size={14} /></button>
                     </div>
                  </div>
               </div>

               <div className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} rounded-xl border ${theme.border} p-3`}>
                  <div className="grid grid-cols-7 gap-1 text-center mb-2">
                     {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                        <div key={i} className={`text-[9px] font-black ${theme.textMuted} uppercase py-1`}>{d}</div>
                     ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                     {getDaysInMonth(selectedDate).map((date, idx) => {
                        if (!date) return <div key={`empty-${idx}`} className="h-8" />;
                        const dateStr = toDateStr(date);
                        const isSelected = pickedDates.includes(dateStr);
                        const isToday = toDateStr(new Date()) === dateStr;
                        const isAssigned = assignments.some(a => a.date === dateStr);

                        return (
                           <button
                              key={dateStr}
                              onClick={() => toggleDate(date)}
                              className={`
                                        relative h-8 rounded-lg flex items-center justify-center transition-all duration-200 border text-[11px] font-bold
                                        ${isSelected
                                    ? 'bg-gradient-to-br from-[#FBD282] to-[#B59143] text-white border-[#B59143] shadow-md transform scale-105'
                                    : isAssigned
                                       ? isDark
                                          ? 'bg-[#1A1C1F] hover:bg-[#2A3441] text-gray-300 border-[#2E2F33] ring-1 ring-green-500/30'
                                          : 'bg-white hover:bg-gray-100 text-gray-700 border-gray-200 ring-1 ring-green-500/30'
                                       : 'opacity-40 cursor-not-allowed bg-gray-100 dark:bg-[#0f1115] text-gray-400 border-transparent'
                                 }
                                        ${isToday && !isSelected ? 'ring-1 ring-[#B59143]/50' : ''}
                                    `}
                           >
                              {date.getDate()}
                              {isSelected && <div className="absolute -top-1 -right-1"><IconWrapper icon={FiCheckCircle} size={10} className="text-white drop-shadow-md" /></div>}
                              {!isSelected && isAssigned && <div className="absolute bottom-0.5 w-1 h-1 rounded-full bg-green-500"></div>}
                           </button>
                        );
                     })}
                  </div>
               </div>
            </div>
         </div>

         {/* Bottom Section: 2 Cols */}
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Floor & Seat Selection */}
            <div className={`${theme.card} border ${theme.border} rounded-2xl p-4 flex flex-col h-full`}>
               <div className="flex items-center justify-between mb-4">
                  <div>
                     <h3 className={`text-sm font-bold ${theme.text}`}>Select Seats</h3>
                     <p className={`text-[10px] ${theme.textMuted} font-semibold`}>Choose floor then select multiple seats</p>
                  </div>
                  <select
                     className={`p-1.5 rounded-lg text-xs font-semibold outline-none border ${theme.border} ${isDark ? 'bg-gray-800 text-white' : 'bg-gray-50 text-gray-900'} focus:ring-1 focus:ring-[#B59143]`}
                     value={selectedFloorId}
                     onChange={(e) => {
                        setSelectedFloorId(e.target.value);
                        setSelectedSeatIds([]);
                        setSelectedUserIds([]);
                     }}
                  >
                     <option value="">-- Choose Floor --</option>
                     {floors.filter(f => f.isVisible).map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                     ))}
                  </select>
               </div>

               <div className={`h-[250px] overflow-y-auto custom-scrollbar p-2 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} rounded-xl border ${theme.border}`}>
                  {!selectedFloorId ? (
                     <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-50">
                        <IconWrapper icon={FiLayout} size={32} className="mb-2" />
                        <span className="text-xs">Select a floor first</span>
                     </div>
                  ) : (
                     <div className="grid grid-cols-4 lg:grid-cols-5 gap-2">
                        {currentFloor?.seats?.map(seat => {
                           const available = isSeatAvailable(seat.id);
                           const selected = selectedSeatIds.includes(seat.id);
                           return (
                              <button
                                 key={seat.id}
                                 disabled={!available}
                                 onClick={() => toggleSeat(seat.id)}
                                 className={`
                                            p-2 rounded-lg text-xs font-bold border transition-all relative
                                            ${!available
                                       ? 'bg-gray-200 dark:bg-gray-800 border-transparent text-gray-400 cursor-not-allowed opacity-50'
                                       : selected
                                          ? 'bg-[#B59143] border-[#B59143] text-white shadow-md'
                                          : isDark
                                             ? 'bg-[#1A1C1F] border-[#2E2F33] hover:border-[#B59143] text-gray-300'
                                             : 'bg-white border-gray-200 hover:border-[#B59143] text-gray-700'
                                    }
                                        `}
                              >
                                 {seat.name}
                                 {selected && <div className="absolute top-0.5 right-0.5"><IconWrapper icon={FiCheck} size={8} /></div>}
                              </button>
                           )
                        })}
                     </div>
                  )}
               </div>
            </div>

            {/* Right: User Selection */}
            <div className={`${theme.card} border ${theme.border} rounded-2xl p-4 flex flex-col h-full`}>
               <div className="flex items-center justify-between mb-4">
                  <div>
                     <h3 className={`text-sm font-bold ${theme.text}`}>Select Users</h3>
                     <p className={`text-[10px] ${theme.textMuted} font-semibold`}>
                        {selectedSeatIds.length === 0
                           ? "Select seats first"
                           : `${selectedUserIds.length}/${selectedSeatIds.length} users selected`}
                     </p>
                  </div>
               </div>

               <div className="relative">
                  <input
                     type="text"
                     placeholder="Search users..."
                     className={`w-full p-2 mb-3 text-xs rounded-lg border ${theme.border} ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50'} focus:ring-1 focus:ring-[#B59143] outline-none`}
                     value={userSearch}
                     onChange={e => setUserSearch(e.target.value)}
                     disabled={selectedSeatIds.length === 0}
                  />

                  <div className={`h-[200px] overflow-y-auto custom-scrollbar space-y-1 ${selectedSeatIds.length === 0 ? 'opacity-50' : ''}`}>
                     {selectedSeatIds.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                           <IconWrapper icon={FiCheckCircle} size={32} className="mb-2" />
                           <span className="text-xs">Select seats to enable users</span>
                        </div>
                     ) : (
                        <>
                           {availableUsers
                              .filter(u => `${u.firstName} ${u.lastName}`.toLowerCase().includes(userSearch.toLowerCase()))
                              .map(u => {
                                 const selected = selectedUserIds.includes(u.id);
                                 const limitReached = selectedUserIds.length >= selectedSeatIds.length;
                                 const disabled = limitReached && !selected;

                                 return (
                                    <button
                                       key={u.id}
                                       onClick={() => !disabled && toggleUser(u.id)}
                                       className={`
                                                w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all border
                                                ${selected
                                             ? 'bg-[#B59143]/10 border-[#B59143]/30'
                                             : isDark
                                                ? 'bg-[#1A1C1F] border-[#2E2F33] hover:bg-[#2A3441]'
                                                : 'bg-white border-gray-100 hover:bg-gray-50'
                                          }
                                                ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                                            `}
                                    >
                                       <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${selected ? 'bg-[#B59143] text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                                          {u.firstName?.[0]}
                                       </div>
                                       <div className="flex-1 min-w-0">
                                          <p className={`text-xs font-bold truncate ${selected ? 'text-[#B59143]' : theme.text}`}>{u.firstName} {u.lastName}</p>
                                          <p className="text-[10px] text-gray-400 truncate">{u.email}</p>
                                       </div>
                                       {selected && <IconWrapper icon={FiCheck} size={14} className="text-[#B59143]" />}
                                    </button>
                                 )
                              })
                           }
                           {availableUsers.length === 0 && (
                              <div className="text-center py-4 text-xs text-gray-400">No available users found.</div>
                           )}
                        </>
                     )}
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default ManagerAssignmentView;
